import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { NewBillingState } from 'src/app/pages/new-billing/store/state';
import { BillingDetailsApiData } from 'src/app/pages/new-billing/types/api.types';
import { AuthState } from 'src/app/store/auth/auth.state';
import { AuthCompany, AuthUser, User } from 'src/models';
import { NavatticExperimentData, NavatticTutorial, NavatticTutorialListData } from '../integrations/navattic-plugin';
import { CookieService } from './cookie/cookie.service';
import { GrowthBookService } from './growthbook/service';
import { UserService } from './user/user.service';

type UserPropertiesWithDotNotation = Partial<User> & Record<string, any>;

@Injectable({
  providedIn: 'root',
})
export class NavatticExperimentService {
  private readonly experimentKey = 'navattic-tutorial-exp';
  private readonly tutorialListKey = 'navattic-tutorial-list';
  public readonly experiment$: Observable<NavatticExperimentData | null>;

  constructor(
    private readonly growthBook: GrowthBookService,
    private readonly store: Store,
    private readonly cookieService: CookieService,
    private readonly userService: UserService,
  ) {
    this.experiment$ = this.growthBook.getFeature<NavatticExperimentData>(this.experimentKey);
  }


  private filterTutorialByConditions(
    company: AuthCompany | undefined,
    tutorial: NavatticTutorial,
    billingDetails?: BillingDetailsApiData,
  ): boolean {
    if (!company) return false;

    const {
      subscriptionStatus,
      trackingType,
      userRole,
      companySize,
      minUserCount,
      pricingPlan,
    } = tutorial;

    const companySubscriptionStatus = company.subscription?.status;
    const billingCustomerStatus = billingDetails?.customerStatus;

    let result = true;
    const failedConditions: string[] = [];

    if (subscriptionStatus) {
      const subscriptionStatusArray = Array.isArray(subscriptionStatus) ? subscriptionStatus : [subscriptionStatus];

      let subscriptionMatch = subscriptionStatusArray.some(status =>
        status === companySubscriptionStatus || status === billingCustomerStatus,
      );

      if (!subscriptionMatch && (companySubscriptionStatus === 'trial' || billingCustomerStatus === 'trial')) {
        subscriptionMatch = subscriptionStatusArray.includes('paid');
      }

      if (!subscriptionMatch) {
        failedConditions.push(
          `subscriptionStatus: expected ${subscriptionStatusArray.join(' or ')}, ` +
          `got company=${companySubscriptionStatus}, billing=${billingCustomerStatus}`,
        );
      }
      result = result && subscriptionMatch;
    }

    if (trackingType && trackingType.length > 0) {
      const trackingModeMatch = trackingType.includes(company.companySettings?.trackingMode);
      if (!trackingModeMatch) {
        failedConditions.push(`trackingType: expected ${trackingType.join(' or ')}, got ${company.companySettings?.trackingMode}`);
      }
      result = result && trackingModeMatch;
    }

    if (userRole && userRole.length > 0) {
      const userRoleMatch = userRole.includes(company.role);
      if (!userRoleMatch) {
        failedConditions.push(`userRole: expected ${userRole.join(' or ')}, got ${company.role}`);
      }
      result = result && userRoleMatch;
    }

    if (pricingPlan && pricingPlan.length > 0) {
      const currentPlanId = billingDetails?.currentPlanId || billingDetails?.planId;
      const currentPlanIdLower = currentPlanId?.toLowerCase() || '';

      const isTrialUser = company.subscription?.status === 'trial' || billingDetails?.customerStatus === 'trial';
      const pricingPlanMatch = currentPlanIdLower
        ? pricingPlan.some(plan => currentPlanIdLower.includes(plan.toLowerCase()))
        : isTrialUser;

      if (!pricingPlanMatch) {
        failedConditions.push(
          `pricingPlan: expected ${pricingPlan.join(' or ')}, ` +
          `got ${currentPlanIdLower || 'none'}, isTrialUser=${isTrialUser}`,
        );
      }
      result = result && pricingPlanMatch;
    }

    if (minUserCount || (companySize && companySize.length > 0)) {
      const currentCompanySize = company.splitTest?.find(test => test.name === 'company-size')?.value;
      const userCountMatch = !isNaN(+minUserCount) ? (company.userCount || 0) >= +minUserCount : true;
      const companySizeMatch = companySize && companySize.length > 0 ? !companySize.includes(currentCompanySize) : true;

      const minUserOrNotCompanySize = userCountMatch || companySizeMatch;
      if (!minUserOrNotCompanySize) {
        failedConditions.push(
          `minUserCount/companySize: minUserCount=${minUserCount}, ` +
          `userCount=${company.userCount}, companySize=${currentCompanySize}, ` +
          `expectedExclude=${companySize}`,
        );
      }
      result = result && minUserOrNotCompanySize;
    }

    return result;
  }


  getTutorialList$(): Observable<NavatticTutorial[]> {
    return this.growthBook.getFeature<{ tutorials: NavatticTutorial[] }>(this.tutorialListKey).pipe(
      map(config => config?.tutorials || []),
      distinctUntilChanged(),
    );
  }

  getTutorialList(): NavatticTutorial[] {
    const config = this.growthBook.getFeatureValue<{ tutorials: NavatticTutorial[] }>(this.tutorialListKey);
    return config?.tutorials || [];
  }

  getActiveTutorial$(variant: 'A' | 'B'): Observable<NavatticTutorial | null> {
    const company$ = this.store.select(AuthState.company);
    const billingDetails$ = this.store.select(NewBillingState.updatedBillingDetails);
    const isVariant$ = this.isVariant$(variant);

    return combineLatest([
      this.growthBook.getFeature<NavatticTutorialListData>(this.tutorialListKey),
      company$,
      billingDetails$,
      isVariant$,
    ]).pipe(
      map(([config, company, billingDetails, isVariant]) => {

        if (!config?.tutorials || !company) {
          return null;
        }

        if (!isVariant) {
          return null;
        }

        const matchingTutorial = config.tutorials.find(tutorial =>
          this.filterTutorialByConditions(company, tutorial, billingDetails),
        );

        return matchingTutorial || null;
      }),
      distinctUntilChanged(),
    );
  }

  isVariant$(variant: 'A' | 'B'): Observable<boolean> {
    return this.experiment$.pipe(
      map(experiment => experiment?.variationid === variant),
      distinctUntilChanged(),
    );
  }

  isExperimentActive$(): Observable<boolean> {
    return this.experiment$.pipe(
      map(experiment => !!experiment),
      distinctUntilChanged(),
    );
  }

  isExperimentActive(): boolean {
    const experiment = this.growthBook.getFeatureValue<NavatticExperimentData>(this.experimentKey);
    return !!experiment;
  }

  getUserIdentificationData(): Record<string, any> | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const user = this.store.selectSnapshot(AuthState.user);
    const company = this.store.selectSnapshot(AuthState.company);

    if (!user || !company) {
      return null;
    }

    const hubspotUtk = this.getHubSpotCookie();
    const identificationData = this.prepareIdentificationData(user, company, hubspotUtk);

    return identificationData;
  }


  private getHubSpotCookie(): string | null {
    return this.cookieService.getCookie('hubspotutk');
  }

  private prepareIdentificationData(user: AuthUser, company: AuthCompany, hubspotUtk: string | null): Record<string, any> {
    const identificationData: Record<string, any> = {
      'user.userId': user.id,
      'user.email': user.email,
      'user.userFullName': user.name,
      'company.companyId': company.id,
      'company.companyName': company.name,
    };

    if (user.timezone) {
      identificationData['user.timezone'] = user.timezone;
    }

    if (company.role) {
      identificationData['user.role'] = company.role;
    }

    if (hubspotUtk) {
      identificationData['user.hubspotutk'] = hubspotUtk;
    }

    if (company.userCount) {
      identificationData['company.userCount'] = company.userCount;
    }

    if (company.subscription?.status) {
      identificationData['company.subscriptionStatus'] = company.subscription.status;
    }

    return identificationData;
  }
}
